import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PartnerBillingExplainer } from "@/components/ShopPricingInfo";
import AddressAutocomplete from "@/components/AddressAutocomplete";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2, Store, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { US_STATES } from "@/lib/usStates";
import { PARTNER_TYPES } from "@/lib/partnerTypes";

export default function ShopSignup() {
  const navigate = useNavigate();
  const [f, setF] = useState({ partner_type: "", partner_category: "", shop_name: "", address: "", city: "", state: "", zip: "", website: "", name: "", email: "", password: "", lat: null, lng: null });
  const [loading, setLoading] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!f.partner_type || !f.partner_category) return toast.error("Please select your partner type and category");
    if (!agreed) return toast.error("Please confirm you understand the Founding Partner setup fee and additional zone pricing");
    if (!f.city.trim() || !f.state) return toast.error("Please enter your city and select a state");
    if (f.partner_type === "business" && !f.address.trim()) return toast.error("Please enter your street address");
    setLoading(true);
    try {
      await api.post("/auth/register/shop", {
        shop_name: f.shop_name, address: f.address, city: f.city, state: f.state, zip: f.zip,
        region: `${f.city}, ${f.state}`,
        website: f.website, name: f.name, partner_type: f.partner_type, partner_category: f.partner_category,
        email: f.email, password: f.password, latitude: f.lat, longitude: f.lng,
      });
      toast.success("Application submitted for review!");
      setSubmitted(true);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || "Signup failed");
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 py-10">
        <div className="w-full max-w-lg">
          <Link to="/" className="flex items-center justify-center mb-6">
            <div className="bg-white rounded-xl px-4 py-3 inline-flex"><img src="/velopaths-logo.png" alt="Velopaths" className="h-8 w-auto" /></div>
          </Link>
          <div className="rounded-2xl border border-border bg-card p-8 text-center" data-testid="shop-signup-success">
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-emerald-500/15"><CheckCircle2 className="w-8 h-8 text-emerald-400" /></div>
            <h1 className="font-heading text-2xl font-bold mb-2">Thank you for applying!</h1>
            <p className="text-sm text-muted-foreground">Your partner application for <span className="text-foreground font-medium">{f.shop_name || "your organization"}</span> has been submitted for review. VeloPaths will email <span className="text-foreground font-medium">{f.email}</span> once it's approved and walk you through the one-time $99 Founding Partner setup fee (your first zone and a year of participation included). You'll be able to sign in after activation.</p>
            <div className="mt-6 flex items-center justify-center gap-3">
              <Button variant="outline" onClick={() => navigate("/")} data-testid="shop-signup-success-home">Back to home</Button>
              <Button onClick={() => navigate("/login")} data-testid="shop-signup-success-login">Go to sign in</Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4 py-10">
      <div className="w-full max-w-lg">
        <Link to="/" className="flex items-center justify-center mb-6">
          <div className="bg-white rounded-xl px-4 py-3 inline-flex"><img src="/velopaths-logo.png" alt="Velopaths" className="h-8 w-auto" /></div>
        </Link>
        <div className="rounded-2xl border border-border bg-card p-8">
          <div className="flex items-center gap-2 mb-1"><Store className="w-5 h-5 text-primary" /><h1 className="font-heading text-2xl font-bold">Become a VeloPaths Partner</h1></div>
          <p className="text-sm text-muted-foreground mb-6">Lease and manage cycling territories anywhere in the US, add advertisers within them, and earn recurring revenue from advertiser activity. New partners are reviewed by VeloPaths before going live.</p>

          <div className="mb-6"><PartnerBillingExplainer setupFee={99} /></div>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label>What type of partner are you?</Label>
              <Select value={f.partner_type} onValueChange={(v) => setF((s) => ({ ...s, partner_type: v, partner_category: "" }))}>
                <SelectTrigger data-testid="signup-partner-type" className="mt-1.5"><SelectValue placeholder="Select partner type" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="business" data-testid="signup-partner-type-business">Business Partner — bike shop, rental, tours</SelectItem>
                  <SelectItem value="trail" data-testid="signup-partner-type-trail">Trail Partner — cyclist, club, advocate, nonprofit, tourism</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {f.partner_type && (
              <div>
                <Label>{f.partner_type === "business" ? "Business type" : "Partner category"}</Label>
                <Select value={f.partner_category} onValueChange={(v) => setF((s) => ({ ...s, partner_category: v }))}>
                  <SelectTrigger data-testid="signup-partner-category" className="mt-1.5"><SelectValue placeholder="Select one" /></SelectTrigger>
                  <SelectContent>
                    {PARTNER_TYPES[f.partner_type].categories.map((c) => <SelectItem key={c.value} value={c.value} data-testid={`signup-partner-category-${c.value}`}>{c.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div>
              <Label>Partner / Organization name</Label>
              <Input value={f.shop_name} onChange={set("shop_name")} required data-testid="shop-signup-name-input" className="mt-1.5" />
            </div>
            <div>
              <Label>Street address{f.partner_type === "trail" ? <span className="text-muted-foreground font-normal"> (optional)</span> : ""}</Label>
              <AddressAutocomplete value={f.address}
                onChange={(v) => setF((s) => ({ ...s, address: v, lat: null, lng: null }))}
                onSelect={(sg) => setF((s) => ({ ...s, address: sg.address || s.address, city: sg.city || s.city, state: US_STATES.includes(sg.state) ? sg.state : s.state, zip: sg.zip || s.zip, lat: sg.lat, lng: sg.lng }))}
                placeholder="Start typing your address…" testId="shop-signup-address-input" className="mt-1.5" />
              {f.partner_type === "business" && <p className="text-xs text-muted-foreground mt-1">Pick your address from the list so your Rider-map pin is exact.</p>}
            </div>
            <div className="grid sm:grid-cols-3 gap-4">
              <div>
                <Label>City</Label>
                <Input value={f.city} onChange={set("city")} required placeholder="Austin" data-testid="shop-signup-city-input" className="mt-1.5" />
              </div>
              <div>
                <Label>State</Label>
                <Select value={f.state} onValueChange={(v) => setF((s) => ({ ...s, state: v }))}>
                  <SelectTrigger data-testid="shop-signup-state-select" className="mt-1.5"><SelectValue placeholder="State" /></SelectTrigger>
                  <SelectContent className="max-h-72">
                    {US_STATES.map((st) => <SelectItem key={st} value={st} data-testid={`shop-signup-state-${st}`}>{st}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>ZIP code</Label>
                <Input value={f.zip} onChange={set("zip")} placeholder="78701" data-testid="shop-signup-zip-input" className="mt-1.5" />
              </div>
            </div>
            <div>
              <Label>Website URL <span className="text-muted-foreground font-normal">(optional)</span></Label>
              <Input type="url" value={f.website} onChange={set("website")} placeholder="https://yourwebsite.com" data-testid="shop-signup-website-input" className="mt-1.5" />
            </div>
            <div className="border-t border-border pt-4">
              <Label>Your name</Label>
              <Input value={f.name} onChange={set("name")} required data-testid="shop-signup-owner-name" className="mt-1.5" />
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label>Email</Label>
                <Input type="email" value={f.email} onChange={set("email")} required data-testid="shop-signup-email" className="mt-1.5" />
              </div>
              <div>
                <Label>Create Password</Label>
                <Input type="password" value={f.password} onChange={set("password")} required data-testid="shop-signup-password" className="mt-1.5" />
              </div>
            </div>
            <label className="flex items-start gap-3 rounded-xl border border-amber-500/30 bg-amber-500/10 p-4 cursor-pointer" data-testid="setup-fee-agreement">
              <Checkbox checked={agreed} onCheckedChange={(v) => setAgreed(!!v)} className="mt-0.5" data-testid="setup-fee-agreement-checkbox" />
              <span className="text-xs text-amber-100/90">I understand that the <strong>$99 Founding Partner setup fee is a one-time fee</strong> that <strong>includes my first zone and one year of participation</strong>, and that <strong>additional zones</strong> are priced separately based on each territory.</span>
            </label>
            <Button type="submit" className="w-full" disabled={loading || !agreed} data-testid="shop-signup-submit-button">
              {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Apply as a Partner
            </Button>
          </form>
          <p className="mt-5 text-sm text-center text-muted-foreground">Already registered? <Link to="/login" className="text-primary hover:underline">Sign in</Link> · <Link to="/application-status" className="text-primary hover:underline" data-testid="check-status-link">Check application status</Link></p>
        </div>
      </div>
    </div>
  );
}
